import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Save } from "lucide-react";
import FFTSettings from "@/pages/FFTSettings";
import IntegrationsSettings from "@/pages/IntegrationsSettings";
import SimulatorPanel from "@/components/SimulatorPanel";

const FIELDS = [
  { key: "HIGH_RISK_REALITY_THRESHOLD", label: "High-risk reality cutoff", min: 0, max: 10, step: 0.1, hint: "Reality score below this triggers HIGH escalation." },
  { key: "HIGH_RISK_GAP_THRESHOLD", label: "High-risk gap trigger", min: 0, max: 10, step: 0.1, hint: "Contradiction gap ≥ this lifts MEDIUM → HIGH." },
  { key: "MEDIUM_RISK_REALITY_THRESHOLD", label: "Medium-risk reality cutoff", min: 0, max: 10, step: 0.1, hint: "Reality below this is at least MEDIUM." },
  { key: "MAX_MISSING_DATA_RATIO", label: "Max missing data ratio", min: 0, max: 1, step: 0.05, hint: "Above this ratio analysis goes silent." },
  { key: "RATE_LIMIT_PER_DEAL_MINUTES", label: "Rate limit per deal (min)", min: 1, max: 60, step: 1, hint: "Minimum minutes between analyses of the same deal." },
];

export default function AdminSettings() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/config");
    setCfg(data);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/config", cfg);
      setCfg(data);
      toast.success("Engine thresholds saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return <div className="p-8 text-sm text-gray-400">Loading config...</div>;

  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="admin-settings">
      <div className="overline text-gray-400 mb-2">Engine Configuration</div>
      <h1 className="font-heading text-4xl font-bold tracking-tight mb-3">
        Decision Layer
      </h1>
      <p className="text-sm text-gray-500 mb-8 leading-relaxed">
        Two engines run in parallel: the Contradiction Engine (rule-based reality vs. intuition)
        and the FFT Anomaly Detector (frequency-domain drift). Tune both below.
      </p>

      <Tabs defaultValue="contradiction" className="w-full">
        <TabsList className="rounded-sm bg-gray-100 border border-gray-200 h-10 mb-6">
          <TabsTrigger value="contradiction" className="rounded-sm data-[state=active]:bg-black data-[state=active]:text-white" data-testid="tab-contradiction">
            Contradiction Engine
          </TabsTrigger>
          <TabsTrigger value="fft" className="rounded-sm data-[state=active]:bg-black data-[state=active]:text-white" data-testid="tab-fft">
            FFT Anomaly Layer
          </TabsTrigger>
          <TabsTrigger value="integrations" className="rounded-sm data-[state=active]:bg-black data-[state=active]:text-white" data-testid="tab-integrations">
            Integrations
          </TabsTrigger>
        </TabsList>

        <TabsContent value="contradiction" className="mt-0">
          <div className="space-y-6 border border-gray-200 bg-white rounded-sm p-8">
            {FIELDS.map((f) => (
              <div key={f.key} className="grid md:grid-cols-[1fr_auto] gap-4 items-start pb-5 border-b border-gray-100 last:border-b-0 last:pb-0">
                <div>
                  <Label className="text-sm font-medium">{f.label}</Label>
                  <div className="text-xs text-gray-500 mt-1 leading-relaxed">{f.hint}</div>
                </div>
                <Input
                  type="number"
                  min={f.min}
                  max={f.max}
                  step={f.step}
                  value={cfg[f.key] ?? ""}
                  onChange={(e) =>
                    setCfg({ ...cfg, [f.key]: e.target.value === "" ? null : Number(e.target.value) })
                  }
                  data-testid={`cfg-${f.key}`}
                  className="w-28 rounded-sm font-mono text-right"
                />
              </div>
            ))}
          </div>

          <div className="mt-6 flex justify-end">
            <Button
              onClick={save}
              disabled={saving}
              data-testid="save-config-btn"
              className="rounded-sm bg-black hover:bg-gray-800 gap-2"
            >
              <Save className="w-3.5 h-3.5" />
              {saving ? "Saving…" : "Save thresholds"}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="fft" className="mt-0">
          <div className="space-y-6">
            <SimulatorPanel />
            <FFTSettings />
          </div>
        </TabsContent>

        <TabsContent value="integrations" className="mt-0">
          <IntegrationsSettings />
        </TabsContent>
      </Tabs>
    </div>
  );
}
